/**
 * Turn `[docN]` citation markers in an answer into links (`citation-N`) that
 * Message.tsx renders as the clickable source label.
 *
 * This runs on the parsed tree rather than on the raw markdown string, and that
 * is the whole point: a marker inside a fenced block or inline backticks lives in
 * a `code`/`inlineCode` node, which has no `text` children, so a text-node-only
 * walk skips it structurally. The previous string-level regex could not tell the
 * difference and happily link-ified markers the user was meant to read literally.
 */

/** Minimal mdast shape — enough to walk without depending on @types/mdast. */
type Node = {
  type: string;
  value?: string;
  url?: string;
  children?: Node[];
};

const MARKER_RE = /\[doc(\d+)\]/g;

/** Split one text node's value into alternating text and citation-link nodes. */
function splitTextNode(node: Node): Node[] | null {
  const value = node.value ?? "";
  MARKER_RE.lastIndex = 0;
  if (!MARKER_RE.test(value)) return null;

  const parts: Node[] = [];
  let cursor = 0;
  MARKER_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = MARKER_RE.exec(value)) !== null) {
    if (match.index > cursor) {
      parts.push({ type: "text", value: value.slice(cursor, match.index) });
    }
    parts.push({
      type: "link",
      url: `citation-${match[1]}`,
      children: [{ type: "text", value: match[0] }],
    });
    cursor = match.index + match[0].length;
  }
  if (cursor < value.length) {
    parts.push({ type: "text", value: value.slice(cursor) });
  }
  return parts;
}

/** Replace text children with their split form, in place, one level. */
function visit(node: Node): void {
  const children = node.children;
  if (!children) return;

  // Never create a link inside a link: markdown cannot nest them, and a marker
  // written inside an existing link text is not a citation of ours.
  const isLink = node.type === "link" || node.type === "linkReference";

  const next: Node[] = [];
  for (const child of children) {
    if (child.type === "text" && !isLink) {
      const split = splitTextNode(child);
      if (split) {
        next.push(...split);
        continue;
      }
    }
    visit(child);
    next.push(child);
  }
  node.children = next;
}

export default function remarkCitations() {
  return (tree: Node) => {
    visit(tree);
  };
}
